import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'hcai_tour_seen_v1';

const STEPS = [
  {
    emoji: '👋',
    title: 'Welcome to HealthClear AI',
    body: 'We turn confusing lab results into plain-language explanations personalized to you. Here\'s a quick tour of what you can do.',
  },
  {
    emoji: '⬆️',
    title: 'Upload a Lab Report',
    body: 'Go to Upload Report and attach a PDF or photo of any lab result. Our AI will analyze it and explain every biomarker in terms you understand.',
    action: { label: 'Go to Upload', path: '/app/upload' },
  },
  {
    emoji: '📋',
    title: 'Read Your Analysis',
    body: 'After uploading, you\'ll get a full breakdown: what\'s normal, what to watch, and questions to ask your doctor.',
  },
  {
    emoji: '📈',
    title: 'Track Trends Over Time',
    body: 'Upload multiple reports and HealthClear AI tracks how your biomarkers change over time — catching patterns before they become problems.',
    action: { label: 'See Trends', path: '/app/trends' },
  },
  {
    emoji: '⚙️',
    title: 'Personalize Your Experience',
    body: 'Visit Profile & Settings to set your language, health literacy level, and background so explanations are tailored just for you.',
    action: { label: 'Open Profile', path: '/app/profile' },
  },
];

export default function WelcomeTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) setVisible(true);
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  const handleAction = (path) => {
    dismiss();
    navigate(path);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] px-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 relative">
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl leading-none"
          aria-label="Skip tour"
        >
          ✕
        </button>

        {/* Step indicator */}
        <div className="flex gap-1.5 mb-5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full flex-1 transition-colors ${
                i <= step ? 'bg-sky-500' : 'bg-slate-200 dark:bg-slate-600'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">{current.emoji}</div>
          <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-2">{current.title}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{current.body}</p>
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          {current.action && (
            <button
              onClick={() => handleAction(current.action.path)}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
            >
              {current.action.label} →
            </button>
          )}
          {isLast ? (
            <button
              onClick={dismiss}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
            >
              Get started
            </button>
          ) : (
            <button
              onClick={() => setStep(s => s + 1)}
              className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${
                current.action
                  ? 'border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  : 'bg-sky-600 hover:bg-sky-700 text-white font-semibold'
              }`}
            >
              {current.action ? 'Next →' : 'Next →'}
            </button>
          )}
          <button
            onClick={dismiss}
            className="w-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xs py-1 transition-colors"
          >
            Skip tour
          </button>
        </div>
      </div>
    </div>
  );
}
